import React from 'react';
import { TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { useRunPlan } from '../context/RunPlanContext';
import { useAnalytics } from '../context/AnalyticsContext';

// Helper function to convert theme class to hex color for charts
const getThemeColor = (themePrimary) => {
  const colorMap = {
    'bg-sky-500': '#0ea5e9',
    'bg-emerald-500': '#10b981',
    'bg-amber-600': '#d97706',
    'bg-emerald-700': '#047857',
    'bg-yellow-700': '#a16207',
    'bg-purple-500': '#a855f7',
    'bg-orange-500': '#f97316'
  };
  return colorMap[themePrimary] || '#0ea5e9'; // Default to sky blue
};

const AnalyticsView = () => {
  const { darkMode, t: theme, getLast7DaysData, getLast4WeeksData, getLast365DaysData, getTotalMileage } = useRunPlan();
  const { getStreakStats } = useAnalytics();
  const streakStats = getStreakStats();

  const chartColor = getThemeColor(theme.primary);

  // Check if there's any data
  const totalMiles = getTotalMileage(365);
  const hasData = totalMiles > 0;

  return (
    <div className="p-4 pb-20">
      <h2 className="text-2xl font-semibold mb-6">Analytics</h2>

      {!hasData ? (
        <div className="flex flex-col items-center justify-center p-12 text-center">
          <div className={'text-6xl mb-4 ' + theme.text}>🐿️</div>
          <h3 className="text-xl font-bold mb-2">No data yet - get scurrying to see your stats!</h3>
          <p className={darkMode ? 'text-slate-400 font-quicksand' : 'text-slate-600 font-quicksand'}>
            Complete some runs and your analytics will appear here. 🌰
          </p>
        </div>
      ) : (
      <div className="space-y-6">
        {/* Weekly Streak Statistics */}
        {streakStats.weekly > 0 && (
          <div className={darkMode ? 'p-5 rounded-2xl border bg-slate-800 border-slate-700' : 'p-5 rounded-2xl border bg-white border-slate-200'}>
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <span className="text-xl">🌰</span>
              Weekly Streak
            </h3>
            <div className="text-center">
              <div className={darkMode ? 'text-5xl font-bold bg-gradient-to-br from-orange-500 to-red-500 bg-clip-text text-transparent' : 'text-5xl font-bold bg-gradient-to-br from-orange-400 to-red-400 bg-clip-text text-transparent'}>
                {streakStats.weekly}
              </div>
              <div className={darkMode ? 'text-lg text-slate-400 mt-2' : 'text-lg text-slate-600 mt-2'}>
                consecutive week{streakStats.weekly !== 1 ? 's' : ''} with activity
              </div>
            </div>
          </div>
        )}
        <div className={darkMode ? 'p-5 rounded-2xl border bg-slate-800 border-slate-700' : 'p-5 rounded-2xl border bg-white border-slate-200'}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className={'w-5 h-5 ' + theme.text} />
              <h3 className="font-semibold">Last 7 Days</h3>
            </div>
            <div className={'text-2xl font-bold ' + theme.text}>{getTotalMileage(7)} mi</div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={getLast7DaysData()}>
              <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
              <XAxis dataKey="date" tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
              <YAxis tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
              <Tooltip
                cursor={false}
                contentStyle={{
                  backgroundColor: darkMode ? '#1e293b' : '#ffffff',
                  border: `1px solid ${darkMode ? '#475569' : '#e5e7eb'}`,
                  borderRadius: '0.5rem',
                  color: darkMode ? '#ffffff' : '#000000'
                }}
              />
              <Bar
                dataKey="miles"
                fill={chartColor}
                radius={[8, 8, 0, 0]}
                activeBar={{ fill: chartColor }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className={darkMode ? 'p-5 rounded-2xl border bg-slate-800 border-slate-700' : 'p-5 rounded-2xl border bg-white border-slate-200'}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className={'w-5 h-5 ' + theme.text} />
              <h3 className="font-semibold">Last 4 Weeks</h3>
            </div>
            <div className={'text-2xl font-bold ' + theme.text}>{getTotalMileage(28)} mi</div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={getLast4WeeksData()}>
              <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
              <XAxis dataKey="week" tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
              <YAxis tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: darkMode ? '#1e293b' : '#ffffff',
                  border: `1px solid ${darkMode ? '#475569' : '#e5e7eb'}`,
                  borderRadius: '0.5rem',
                  color: darkMode ? '#ffffff' : '#000000'
                }}
              />
              <Line type="monotone" dataKey="miles" stroke={chartColor} strokeWidth={3} dot={{ fill: chartColor, r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className={darkMode ? 'p-5 rounded-2xl border bg-slate-800 border-slate-700' : 'p-5 rounded-2xl border bg-white border-slate-200'}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className={'w-5 h-5 ' + theme.text} />
              <h3 className="font-semibold">Last 365 Days</h3>
            </div>
            <div className={'text-2xl font-bold ' + theme.text}>{getTotalMileage(365)} mi</div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={getLast365DaysData()}>
              <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
              <XAxis dataKey="month" tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
              <YAxis tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: darkMode ? '#1e293b' : '#ffffff',
                  border: `1px solid ${darkMode ? '#475569' : '#e5e7eb'}`,
                  borderRadius: '0.5rem',
                  color: darkMode ? '#ffffff' : '#000000'
                }}
              />
              <Line type="monotone" dataKey="miles" stroke={chartColor} strokeWidth={3} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      )}
    </div>
  );
};

export default AnalyticsView;
